/**
 * QA History Modal
 * Shows audit log history of all QA changes with pagination and filtering
 */

import React, { useEffect, useState, useCallback } from 'react';
import { advisorApiService } from '../../services/advisor-api';
import type { QAAuditLog } from '../../types/qa-management';
import '../../styles/QAHistoryModal.css';

interface QAHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ITEMS_PER_PAGE = 20;

export const QAHistoryModal: React.FC<QAHistoryModalProps> = ({ isOpen, onClose }) => {
  const [auditLogs, setAuditLogs] = useState<QAAuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterOperation, setFilterOperation] = useState<'ALL' | 'CREATE' | 'UPDATE' | 'DELETE'>(
    'ALL'
  );
  const [filterUsername, setFilterUsername] = useState<string>('ALL');

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async (page: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const offset = (page - 1) * ITEMS_PER_PAGE;
      const response = await advisorApiService.getQAAuditLogs(ITEMS_PER_PAGE, offset);

      if (response.data) {
        setAuditLogs(response.data.audit_logs);
        setTotalCount(response.data.pagination.total);
      } else {
        setError(response.error || 'Failed to load audit logs');
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('An error occurred while loading audit logs');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true); // Set loading immediately when opening
      setCurrentPage(1);
      setError(null); // Clear any previous errors
      fetchAuditLogs(1);
    } else {
      // Reset state when modal closes
      setAuditLogs([]);
      setIsLoading(false);
      setError(null);
      setFilterOperation('ALL');
      setFilterUsername('ALL');
    }
  }, [isOpen, fetchAuditLogs]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchAuditLogs(newPage);
  };

  // Get unique usernames from audit logs
  const uniqueUsernames = Array.from(new Set(auditLogs.map((log) => log.username))).sort();

  // Filter logs based on operation type and username
  const filteredLogs = auditLogs.filter((log) => {
    const operationMatch = filterOperation === 'ALL' || log.operation_type === filterOperation;
    const usernameMatch = filterUsername === 'ALL' || log.username === filterUsername;
    return operationMatch && usernameMatch;
  });

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Get operation badge class
  const getOperationClass = (operation: string) => {
    switch (operation) {
      case 'CREATE':
        return 'operation-create';
      case 'UPDATE':
        return 'operation-update';
      case 'DELETE':
        return 'operation-delete';
      default:
        return '';
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  if (!isOpen) {
    return null;
  }

  return (
    <div className="qa-history-modal-overlay" onClick={onClose}>
      <div className="qa-history-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="qa-history-modal-header">
          <h2>Question Answer Change History</h2>
          <button
            type="button"
            className="qa-history-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Filter Info Bar */}
        <div className="qa-history-filter-bar">
          <div className="qa-history-filter-count">
            Showing {filteredLogs.length} of {totalCount} total changes
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="qa-history-loading">
            <p>Loading audit logs...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="qa-history-error">
            <p>{error}</p>
          </div>
        )}

        {/* Audit Logs Table */}
        {!isLoading && !error && (
          <>
            {filteredLogs.length === 0 ? (
              <div className="qa-history-empty">
                <p>
                  No {filterOperation === 'ALL' ? '' : filterOperation.toLowerCase()} operations
                  found on this page.
                </p>
              </div>
            ) : (
              <div className="qa-history-table-wrapper">
                <table className="qa-history-table">
                  <thead>
                    <tr>
                      <th style={{ width: '12%' }}>
                        <div className="qa-history-filter-header">
                          <span className="qa-history-filter-label">Username</span>
                          <select
                            className="qa-history-filter-dropdown"
                            value={filterUsername}
                            onChange={(e) => setFilterUsername(e.target.value)}
                          >
                            <option value="ALL">All Users</option>
                            {uniqueUsernames.map((username) => (
                              <option key={username} value={username}>
                                {username}
                              </option>
                            ))}
                          </select>
                        </div>
                      </th>
                      <th style={{ width: '14%' }}>
                        <div className="qa-history-filter-header">
                          <span className="qa-history-filter-label">Operation</span>
                          <select
                            className="qa-history-filter-dropdown"
                            value={filterOperation}
                            onChange={(e) =>
                              setFilterOperation(
                                e.target.value as 'ALL' | 'CREATE' | 'UPDATE' | 'DELETE'
                              )
                            }
                          >
                            <option value="ALL">All Operations</option>
                            <option value="CREATE">Create</option>
                            <option value="UPDATE">Update</option>
                            <option value="DELETE">Delete</option>
                          </select>
                        </div>
                      </th>
                      <th style={{ width: '28%' }}>Question</th>
                      <th style={{ width: '30%' }}>Changes</th>
                      <th style={{ width: '16%' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log) => {
                      const before = log.question_artifact_before;
                      const after = log.question_artifact_after;

                      // Helper to compare arrays
                      const arraysEqual = (a: string[] | undefined, b: string[] | undefined) => {
                        if (!a && !b) return true;
                        if (!a || !b) return false;
                        if (a.length !== b.length) return false;
                        const sortedA = [...a].sort();
                        const sortedB = [...b].sort();
                        return sortedA.every((val, idx) => val === sortedB[idx]);
                      };

                      // Detect what changed
                      const questionChanged = before?.question !== after?.question;
                      const answerChanged = before?.answer !== after?.answer;
                      const programCodesChanged = !arraysEqual(
                        before?.program_codes,
                        after?.program_codes
                      );
                      const statusesChanged = !arraysEqual(before?.statuses, after?.statuses);
                      const visaReqChanged = !arraysEqual(
                        before?.visa_requirements,
                        after?.visa_requirements
                      );
                      // Rows logged before the is_included column existed carry undefined → included
                      const includedLabel = (v: boolean | undefined) =>
                        v === false ? 'No' : 'Yes';
                      const includedChanged =
                        includedLabel(before?.is_included) !== includedLabel(after?.is_included);

                      return (
                        <tr key={log.operation_id} className="qa-history-row">
                          <td>
                            <div className="qa-history-username">{log.username}</div>
                          </td>
                          <td>
                            <span
                              className={`qa-history-operation-badge ${getOperationClass(log.operation_type)}`}
                            >
                              {log.operation_type}
                            </span>
                          </td>
                          <td>
                            <div className="qa-history-question-id">
                              {after?.question ?? before?.question ?? '—'}
                            </div>
                          </td>
                          <td>
                            <div className="qa-history-changes">
                              {log.operation_type === 'CREATE' && after && (
                                <>
                                  <div className="change-item">
                                    <div className="change-label">Question:</div>
                                    <div className="change-value added">{after.question}</div>
                                  </div>
                                  <div className="change-item">
                                    <div className="change-label">Answer:</div>
                                    <div className="change-value added">{after.answer}</div>
                                  </div>
                                  <div className="change-item">
                                    <div className="change-label">Metadata:</div>
                                    <div className="change-value added">
                                      Programs: {after.program_codes?.join(', ') || 'N/A'} |
                                      Statuses: {after.statuses?.join(', ') || 'N/A'} | Visa:{' '}
                                      {after.visa_requirements?.join(', ') || 'N/A'}
                                    </div>
                                  </div>
                                </>
                              )}
                              {log.operation_type === 'DELETE' && before && (
                                <>
                                  <div className="change-item">
                                    <div className="change-label">Question:</div>
                                    <div className="change-value removed">{before.question}</div>
                                  </div>
                                  <div className="change-item">
                                    <div className="change-label">Answer:</div>
                                    <div className="change-value removed">{before.answer}</div>
                                  </div>
                                  <div className="change-item">
                                    <div className="change-label">Metadata:</div>
                                    <div className="change-value removed">
                                      Programs: {before.program_codes?.join(', ') || 'N/A'} |
                                      Statuses: {before.statuses?.join(', ') || 'N/A'} | Visa:{' '}
                                      {before.visa_requirements?.join(', ') || 'N/A'}
                                    </div>
                                  </div>
                                </>
                              )}
                              {log.operation_type === 'UPDATE' && before && after && (
                                <>
                                  {questionChanged && (
                                    <div className="change-item">
                                      <div className="change-label">Question:</div>
                                      <div className="change-value removed">{before.question}</div>
                                      <div className="change-value added">{after.question}</div>
                                    </div>
                                  )}
                                  {answerChanged && (
                                    <div className="change-item">
                                      <div className="change-label">Answer:</div>
                                      <div className="change-value removed">{before.answer}</div>
                                      <div className="change-value added">{after.answer}</div>
                                    </div>
                                  )}
                                  {programCodesChanged && (
                                    <div className="change-item">
                                      <div className="change-label">Program Codes:</div>
                                      <div className="change-value removed">
                                        {before.program_codes?.join(', ') || 'N/A'}
                                      </div>
                                      <div className="change-value added">
                                        {after.program_codes?.join(', ') || 'N/A'}
                                      </div>
                                    </div>
                                  )}
                                  {statusesChanged && (
                                    <div className="change-item">
                                      <div className="change-label">Statuses:</div>
                                      <div className="change-value removed">
                                        {before.statuses?.join(', ') || 'N/A'}
                                      </div>
                                      <div className="change-value added">
                                        {after.statuses?.join(', ') || 'N/A'}
                                      </div>
                                    </div>
                                  )}
                                  {visaReqChanged && (
                                    <div className="change-item">
                                      <div className="change-label">Visa Requirements:</div>
                                      <div className="change-value removed">
                                        {before.visa_requirements?.join(', ') || 'N/A'}
                                      </div>
                                      <div className="change-value added">
                                        {after.visa_requirements?.join(', ') || 'N/A'}
                                      </div>
                                    </div>
                                  )}
                                  {includedChanged && (
                                    <div className="change-item">
                                      <div className="change-label">Include in Chatbot:</div>
                                      <div className="change-value removed">
                                        {includedLabel(before.is_included)}
                                      </div>
                                      <div className="change-value added">
                                        {includedLabel(after.is_included)}
                                      </div>
                                    </div>
                                  )}
                                  {!questionChanged &&
                                    !answerChanged &&
                                    !programCodesChanged &&
                                    !statusesChanged &&
                                    !visaReqChanged &&
                                    !includedChanged && (
                                      <div className="change-item">
                                        <div className="change-value neutral">
                                          No changes detected
                                        </div>
                                      </div>
                                    )}
                                </>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="qa-history-date">{formatDate(log.created_at)}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="qa-history-pagination">
                <button
                  type="button"
                  className="qa-history-pagination-button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!hasPrevPage || isLoading}
                >
                  ← Previous
                </button>

                <span className="qa-history-pagination-info">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  type="button"
                  className="qa-history-pagination-button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!hasNextPage || isLoading}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
